import React from 'react';
import { Modal } from '@brighterion/br-ui';
import { useTranslation } from 'react-i18next';
import { Formik, FormikHelpers } from 'formik';
import useCreateTenantGroup from 'Hooks/query-hooks/tenant-groups/useCreateTenantGroup';
import useGetTenantGroups from 'Hooks/query-hooks/tenant-groups/useGetTenantGroups';
import { FormikInputField } from 'Components/FormikComponents';
import { EventNames, EventTypes, trackEvent } from '@brighterion/insights';
import { tenantGroupValidationSchema } from './tenantGroupValidationSchema';

import styles from './AddTenantGroupModal.css';

type AddTenantGroupModalProps = {
    isOpen: boolean;
    closeModal: () => void;
};

const AddTenantGroupModal = ({ isOpen, closeModal }: AddTenantGroupModalProps) => {
    const { t } = useTranslation();
    const { data: tenantGroupData } = useGetTenantGroups({});

    const mutation = useCreateTenantGroup({
        mutationOptions: {
            onError: () => {
                closeModal();
            },
            onSuccess: () => {
                closeModal();
            }
        }
    });
    const existingTenantGroupNames: string[] = React.useMemo(() => {
        return tenantGroupData?.original.map((entry) => entry.name!.toLocaleLowerCase()) || [];
    }, [tenantGroupData]);

    /** Check if the modal is open */
    if (!isOpen) return null;

    const initialValues: TenantGroup = {
        name: '',
        description: ''
    };

    return (
        <Modal isOpen={isOpen} closeModal={closeModal} closeOnClickOutside={false}>
            <Formik
                initialValues={initialValues}
                validationSchema={tenantGroupValidationSchema(t, existingTenantGroupNames)}
                onSubmit={(formValues: TenantGroup, actions: FormikHelpers<{}>) => {
                    mutation.mutate(formValues);
                    actions.setSubmitting(false);
                    trackEvent({
                        eventType: EventTypes.track,
                        eventName: EventNames.click,
                        payload: {
                            clickIdentifier: 'Tenant Groups - Add Tenant Group',
                            text: 'Add Tenant Group'
                        }
                    });
                }}
            >
                {({ handleChange, handleBlur, handleSubmit, isSubmitting, isValid, dirty }) => {
                    const disableOnAction = mutation.isLoading || isSubmitting || !dirty || !isValid;

                    return (
                        <>
                            <Modal.Title
                                id='add-tenant-group-modal-title'
                                onClose={closeModal}
                                title={t('Add Tenant Group')}
                            />
                            <div className={styles.formContainer}>
                                <FormikInputField
                                    className={styles.inputField}
                                    handleBlur={handleBlur}
                                    handleChange={handleChange}
                                    label={t('Tenant Group Name')}
                                    name='name'
                                />
                                <FormikInputField
                                    className={styles.inputField}
                                    handleBlur={handleBlur}
                                    handleChange={handleChange}
                                    label={t('Description (Optional)')}
                                    name='description'
                                />
                            </div>
                            <Modal.Footer
                                id='add-tenant-group-modal-footer'
                                disableOnAction={disableOnAction}
                                disableOnCancel={mutation.isLoading}
                                onAction={handleSubmit}
                                onActionText={t('Save')}
                                onClose={closeModal}
                                onCloseText={t('Cancel')}
                            />
                        </>
                    );
                }}
            </Formik>
        </Modal>
    );
};

export default AddTenantGroupModal;
